/**
 * Checklist row normalizer, resolver classifier, and the non-authoritative
 * suggestion write model (checklist-reference).
 *
 * PURE module — no AWS SDK, no Node-only API, no browser-only API. Consumed by
 * the ingest Lambda (normalize each Master Checklist row into a canonical
 * Checklist_Entry), the Checklist_Resolver in the catalog handler (classify a
 * (product, number) Query into single/multiple/none), and property tests.
 *
 * The natural-key codec + normalization primitives live in `./checklist-key`;
 * this module reuses `normalizeNumber` / `normalizeProductName` so that a stored
 * entry's key and a resolve-time lookup land on the same canonical value.
 */

import type { CardIdentity, ChecklistSourceReference } from './types';
import { SERIAL_TO_INGEST_MAX, SERIAL_TO_MIN } from './types';
import type { ChecklistNaturalKey } from './checklist-key';
import { normalizeNumber, normalizeProductName } from './checklist-key';

/**
 * One raw row of the Master Checklist, already column-mapped by header name.
 * `cardSet`, `number`, and `athlete` are the required cells; `team` and
 * `sequence` are optional. All values arrive as raw strings (leading/trailing
 * whitespace not yet trimmed).
 */
export interface ChecklistRow {
  /** CARD SET — the full parallel/variety string (verbatim, Parallel id). */
  cardSet: string;
  /** CARD NUMBER — the card number within the set. */
  number: string;
  /** ATHLETE — the player/card name. */
  athlete: string;
  /** TEAM — optional. */
  team?: string;
  /** SEQUENCE — optional serial print run (`10` = /10, `1` = a 1/1, blank = base). */
  sequence?: string;
}

/**
 * The `{ year, brand, product }` triple identifying the checklist's product,
 * supplied by the operator at ingest (not a column in the sheet).
 */
export interface ProductDescriptor {
  year: string;
  brand: string;
  product: string;
}

/**
 * Partition key containing the compact, operator-facing checklist product
 * directory. Checklist entries stay partitioned by their product; this separate
 * metadata partition lets Review populate a dropdown without scanning every
 * card variation in the reference table.
 */
export const CHECKLIST_PRODUCTS_PK = 'CHECKLIST_PRODUCTS';

/** Prefix-partitioned product index used by Review without loading the directory. */
export const CHECKLIST_PRODUCT_SEARCH_PK_PREFIX = 'CHECKLIST_PRODUCT_SEARCH#';

const CHECKLIST_SEARCH_MIN_PREFIX = 2;
const CHECKLIST_SEARCH_MAX_TERM = 80;
const CHECKLIST_SEARCH_STOP_WORDS = new Set([
  'card',
  'cards',
  'checklist',
  'set',
  'the',
  'trading',
]);

/** Normalize operator/OCR text into the terms used by the checklist product index. */
export function checklistProductSearchTerms(value: string): string[] {
  return [
    ...new Set(
      value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('en-US')
        .split(/[^a-z0-9]+/)
        .filter((term) => term.length >= CHECKLIST_SEARCH_MIN_PREFIX)
        .map((term) => term.slice(0, CHECKLIST_SEARCH_MAX_TERM)),
    ),
  ];
}

/** Terms that carry useful product identity in an operator-entered checklist query. */
export function checklistProductQueryTerms(value: string): string[] {
  const terms = checklistProductSearchTerms(value);
  const meaningful = terms.filter((term) => !CHECKLIST_SEARCH_STOP_WORDS.has(term));
  return meaningful.length > 0 ? meaningful : terms;
}

function boundedEditDistance(left: string, right: string, maximum: number): number | undefined {
  if (Math.abs(left.length - right.length) > maximum) return undefined;
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
    const current = [leftIndex];
    let rowMinimum = leftIndex;
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
      const substitution =
        previous[rightIndex - 1]! + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1);
      const value = Math.min(previous[rightIndex]! + 1, current[rightIndex - 1]! + 1, substitution);
      current.push(value);
      rowMinimum = Math.min(rowMinimum, value);
    }
    if (rowMinimum > maximum) return undefined;
    previous = current;
  }
  const distance = previous[right.length]!;
  return distance <= maximum ? distance : undefined;
}

function checklistSearchTermScore(query: string, candidate: string): number | undefined {
  if (query === candidate) return 0;
  if (candidate.startsWith(query)) return 2 + Math.min(candidate.length - query.length, 4);
  if (!/^\d+$/.test(query) && !/^\d+$/.test(candidate) && candidate.includes(query)) {
    return 7 + Math.min(candidate.indexOf(query), 3);
  }
  if (query.startsWith(candidate) && candidate.length >= 3) {
    return 5 + Math.min(query.length - candidate.length, 4);
  }
  if (/^\d+$/.test(query) || /^\d+$/.test(candidate) || query.length < 4) return undefined;
  const maximumDistance = query.length >= 7 ? 2 : 1;
  const distance = boundedEditDistance(query, candidate, maximumDistance);
  return distance === undefined ? undefined : 10 + distance * 3;
}

/**
 * Rank a checklist label against an operator query. Lower scores are better;
 * `undefined` means that at least one meaningful query term has no safe match.
 * Numeric identifiers deliberately remain exact/prefix-only.
 */
export function checklistProductSearchScore(
  query: string,
  candidateText: string,
): number | undefined {
  const queryTerms = checklistProductQueryTerms(query);
  if (queryTerms.length === 0) return 0;
  const candidateTerms = checklistProductSearchTerms(candidateText);
  let score = 0;
  for (const queryTerm of queryTerms) {
    let best: number | undefined;
    for (const candidateTerm of candidateTerms) {
      const candidateScore = checklistSearchTermScore(queryTerm, candidateTerm);
      if (candidateScore !== undefined && (best === undefined || candidateScore < best)) {
        best = candidateScore;
      }
    }
    if (best === undefined) return undefined;
    score += best;
  }
  return score;
}

/** Bucket a normalized search term by its first two characters. */
export function checklistProductSearchPk(term: string): string {
  return `${CHECKLIST_PRODUCT_SEARCH_PK_PREFIX}${term.slice(0, CHECKLIST_SEARCH_MIN_PREFIX)}`;
}

/** Sort key that supports `begins_with` type-ahead queries within a term bucket. */
export function checklistProductSearchSk(term: string, checklistId: string): string {
  return `${term}#${checklistId}`;
}

/** Current deterministic card-level OCR + visual-evidence product matcher cache version. */
export const CHECKLIST_PRODUCT_SUGGESTION_MODEL_ID = 'deterministic-checklist-v35';

/** Low-cost multimodal model used only after deterministic text evidence cannot resolve a card. */
export const CHECKLIST_IMAGE_EVIDENCE_MODEL_ID = 'us.amazon.nova-lite-v1:0';

/** Higher-quality visual pass used only when Nova Lite still leaves the card unresolved. */
export const CHECKLIST_IMAGE_EVIDENCE_ESCALATION_MODEL_ID = 'us.openai.gpt-5.6-luna';

/** Available higher-quality fallback while the preferred escalation model is account-blocked. */
export const CHECKLIST_IMAGE_EVIDENCE_FALLBACK_MODEL_ID = 'us.amazon.nova-pro-v1:0';
export const CHECKLIST_IMAGE_EVIDENCE_MODEL_IDS = [
  CHECKLIST_IMAGE_EVIDENCE_MODEL_ID,
  CHECKLIST_IMAGE_EVIDENCE_ESCALATION_MODEL_ID,
  CHECKLIST_IMAGE_EVIDENCE_FALLBACK_MODEL_ID,
] as const;

/** Version of the bounded visual-evidence extraction contract stored on Card_Record. */
export const CHECKLIST_IMAGE_EVIDENCE_VERSION = 'checklist-image-evidence-v2';

/** Hard cost and payload bounds for one fallback extraction. */
export const CHECKLIST_IMAGE_EVIDENCE_MAX_OUTPUT_TOKENS = 320;
export const CHECKLIST_IMAGE_EVIDENCE_MAX_BYTES_PER_SIDE = 3_500_000;
export const CHECKLIST_IMAGE_EVIDENCE_TIMEOUT_MS = 15_000;

/** Prefix for status-specific, bounded checklist defect queue partitions. */
export const CHECKLIST_ISSUES_PK = 'CHECKLIST_ISSUES';

export const CHECKLIST_ISSUE_TYPES = [
  'MISSING_CARD',
  'INCORRECT_CARD',
  'MISSING_SUBSET',
  'INCORRECT_SUBSET',
  'MISSING_PARALLEL',
  'INCORRECT_PARALLEL',
  'OTHER',
] as const;

export type ChecklistIssueType = (typeof CHECKLIST_ISSUE_TYPES)[number];
export type ChecklistIssueStatus = 'OPEN' | 'RESOLVED';

export function checklistIssuesPk(status: ChecklistIssueStatus): string {
  return `${CHECKLIST_ISSUES_PK}#${status}`;
}

/** Context captured from Review when an operator reports a checklist defect. */
export interface ChecklistIssueContext {
  checklistId?: string;
  checklistLabel?: string;
  cardNumber?: string;
  subset?: string;
  parallel?: string;
}

/** Durable checklist-engineering work item stored in the checklist table. */
export interface ChecklistIssue {
  issueKey: string;
  issueId: string;
  issueType: ChecklistIssueType;
  status: ChecklistIssueStatus;
  cardId: string;
  batchId: string;
  note?: string;
  context: ChecklistIssueContext;
  createdAt: string;
  createdBy: string;
  reporterEmail?: string;
  /** Administrator-authored, reviewer-safe resolution or workaround text. */
  reviewerGuidance?: string;
  reviewerGuidanceUpdatedAt?: string;
  reviewerGuidanceUpdatedBy?: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

export interface CreateChecklistIssueRequest {
  issueType: ChecklistIssueType;
  cardId: string;
  batchId: string;
  note?: string;
  context?: ChecklistIssueContext;
}

export interface ChecklistIssuesResponse {
  items: ChecklistIssue[];
  truncated: boolean;
}

/** Sanitized issue guidance that may be shown to a reviewer. */
export interface ChecklistIssueGuidance {
  issueType: ChecklistIssueType;
  status: ChecklistIssueStatus;
  context: ChecklistIssueContext;
  guidance: string;
}

export interface ChecklistIssueGuidanceResponse {
  items: ChecklistIssueGuidance[];
}

/**
 * A selectable product in the Review checklist dropdown. `checklistId` is the
 * canonical checklist product partition key and is intentionally opaque to the
 * browser; the API re-reads it before applying a run context.
 */
export interface ReviewChecklistProduct {
  checklistId: string;
  product: ProductDescriptor;
  category: string;
  /** Product language when it can be derived without guessing. */
  language?: string;
  source?: {
    system: 'azure-sql-ebay';
    targetId: string;
  };
}

/**
 * The human-selected checklist constraint for one Storage_Batch. For capture
 * runs it lives on the run's Storage_Batch; for manual/no-run batches it lives
 * on the manually created Storage_Batch. It never asserts card identity.
 */
export interface RunChecklistContext extends ReviewChecklistProduct {
  assignedAt: string;
  assignedBy: string;
}

/** One imported checklist ranked from OCR evidence for operator confirmation. */
export interface ChecklistContextSuggestionCandidate extends ReviewChecklistProduct {
  /** Model confidence in [0, 1]. It is advisory, never an authority threshold. */
  confidence: number;
  /** Short OCR-supported reasons for the ranking; no raw OCR payload is stored. */
  evidence: string[];
}

/**
 * Non-authoritative checklist-product hint derived from stored OCR. New
 * suggestions are card-level and therefore have `sampledCardCount = 1`.
 * Legacy Storage_Batch suggestions may contain a larger sampled count.
 * Only ids from the imported checklist directory may appear in `candidates`.
 */
export interface ChecklistContextSuggestion {
  candidates: ChecklistContextSuggestionCandidate[];
  sampledCardCount: number;
  generatedAt: string;
  model: string;
}

/** Recognizable OCR evidence when no imported checklist can be recommended. */
export interface ChecklistSuggestionObservation {
  label: string;
  evidence: string[];
}

export type ChecklistSuggestionStatus =
  | 'ready'
  | 'no-ocr'
  | 'no-candidates'
  | 'no-confident-match'
  | 'model-failed';

export type ChecklistSuggestionFailureReason =
  | 'missing-product-evidence'
  | 'below-confidence-threshold'
  | 'ambiguous-products'
  | 'ambiguous-roster';

/** Durable explanation for the latest non-authoritative product no-match. */
export interface ChecklistProductSuggestionFailure {
  status: 'no-candidates' | 'no-confident-match';
  reason: ChecklistSuggestionFailureReason;
  generatedAt: string;
  model: string;
  observation?: ChecklistSuggestionObservation;
  /** Ranked, non-authoritative alternatives retained for operator review. */
  candidates?: ChecklistContextSuggestionCandidate[];
}

/** Outcome counters from applying a saved checklist constraint to a run. */
export interface RunChecklistResolutionSummary {
  cardsConsidered: number;
  suggested: number;
  parallelChoices: number;
  noNumber: number;
  noMatch: number;
  skippedReviewed: number;
  truncated: boolean;
}

/** Response shared by the run checklist read and apply endpoints. */
export interface RunChecklistResponse {
  runId: string;
  context?: RunChecklistContext;
  suggestion?: ChecklistContextSuggestion;
  suggestionStatus?: ChecklistSuggestionStatus;
  observation?: ChecklistSuggestionObservation;
  summary?: RunChecklistResolutionSummary;
}

/** Response shared by the storage-batch checklist read and apply endpoints. */
export interface BatchChecklistResponse {
  batchId: string;
  /** True only for a write-free onboarding preview. */
  preview?: boolean;
  context?: RunChecklistContext;
  suggestion?: ChecklistContextSuggestion;
  suggestionStatus?: ChecklistSuggestionStatus;
  observation?: ChecklistSuggestionObservation;
  summary?: RunChecklistResolutionSummary;
}

export interface BatchParallelSelectionSummary {
  requested: number;
  updated: number;
  skippedReviewed: number;
  notInBatch: number;
  noMatch: number;
}

export interface BatchParallelSelectionResponse {
  batchId: string;
  variation: string;
  updatedCardIds: string[];
  summary: BatchParallelSelectionSummary;
}

/**
 * A stored reference item: the canonical `CardIdentity` (including `variation` =
 * the verbatim CARD SET and, when defined, `serialTo`) plus the Natural_Key
 * tuple `(year, brand, product, number, cardSet)` used to key it in the
 * Checklist_Reference_Table.
 */
/** Sanitized display advice; never a source-write authorization or client proof. */
export interface ChecklistPlainVersionPolicy {
  decision: 'allowed' | 'requires-parallel' | 'unknown';
  /** Public reason code only; no raw overlay, lock or source record. */
  reason?: string;
}

/** Exact resolved parent subset advice, including no-row browse contexts. */
export interface ChecklistSubsetPolicy {
  subsetDefinition?: import('./checklist-source').ChecklistSubsetDefinition;
  plainVersion: ChecklistPlainVersionPolicy;
  /** Display/cache digest only; authoritative saves resolve and fence current evidence. */
  revision?: string;
}

/** Server-projected, source-qualified row policy; never accept this back as authority. */
export interface ChecklistSelectionPolicy extends ChecklistSubsetPolicy {
  rowTreatmentStatus: 'PLAIN' | 'INTRINSIC' | 'UNKNOWN';
  /** Present only after exact PARALLEL definition/id resolution, never variation-text inference. */
  intrinsicTreatment?: { definitionId: string; name: string };
  /** Exact effective durable fields, including false and cleared/absent values. */
  correctedIdentityFields: (keyof CardIdentity)[];
  /** True only for a proven uncorrected generated alias on a validated PLAIN row. */
  rowVariationIsGeneratedAlias?: true;
}

export interface ChecklistEntry {
  /** Advisory projection only; the server independently validates every authoritative save. */
  selectionPolicy?: ChecklistSelectionPolicy;
  unresolvedClassification?: import('./checklist-unresolved').ChecklistUnresolvedClassification;
  identity: CardIdentity;
  key: ChecklistNaturalKey;
  /** Optional source-provided strings normalized for marketplace title use. */
  titleSafe?: {
    name?: string;
    subset?: string;
    variation?: string;
  };
  /** Imported source ownership, when this entry came from a versioned snapshot. */
  source?: ChecklistSourceReference;
}

/**
 * The outcome of normalizing one Checklist_Row (Req 4.1–4.7):
 *  - `entry`  — a canonical Checklist_Entry, with an optional `bad-sequence`
 *    warning when the SEQUENCE cell was present but not an accepted integer.
 *  - `skip`   — the row is missing a required cell (CARD SET / CARD NUMBER /
 *    ATHLETE) and produces no entry.
 */
export type NormalizeOutcome =
  | { kind: 'entry'; entry: ChecklistEntry; warning?: 'bad-sequence' }
  | { kind: 'skip'; reason: 'missing-required-cell' };

/**
 * Classification of a resolve over the entries a (product, number) Query
 * returned (Req 7.2–7.4, 8.1–8.3):
 *  - `single`   — exactly one entry matched → becomes the `checklistSuggestion`.
 *  - `multiple` — two or more matched → the Parallel menu (no write).
 *  - `none`     — zero matched (or the number was absent/empty).
 */
export type ChecklistResolution =
  | { kind: 'single'; entry: ChecklistEntry }
  | { kind: 'multiple'; candidates: ChecklistEntry[] }
  | { kind: 'none' };

/** Add an optional identity field only when it is populated (never `''`/null). */
function assignIfPresent(
  identity: CardIdentity,
  field: 'year' | 'brand' | 'team' | 'variation',
  value: string,
): void {
  if (value !== '') {
    identity[field] = value;
  }
}

/**
 * Normalize a `ChecklistRow` + `ProductDescriptor` + `category` into a canonical
 * Checklist_Entry (Req 4.1–4.7):
 *
 * - CARD NUMBER → `number`, ATHLETE → `name`, TEAM → `team`, each with leading
 *   and trailing whitespace trimmed (Req 4.1).
 * - CARD SET → `variation`, preserved verbatim with only leading/trailing
 *   whitespace trimmed — internal punctuation/spacing kept, NO parallel split
 *   (Req 4.2).
 * - `year` / `brand` / `set` from the Product_Descriptor; `category` from the
 *   ingest argument (Req 4.3).
 * - SEQUENCE: blank/whitespace → omit `serialTo` (Req 4.4); an integer in
 *   `[SERIAL_TO_MIN, SERIAL_TO_INGEST_MAX]` → `serialTo` (Req 4.5); any other
 *   present value (zero, negative, non-integer, non-numeric, out of range) →
 *   omit `serialTo` and flag a `bad-sequence` warning while still producing the
 *   entry (Req 4.6). `serialTo` is NEVER stored as `0`/`null`/`''` (Req 1.3).
 * - A missing (empty or whitespace-only) required cell — CARD SET, CARD NUMBER,
 *   or ATHLETE — yields a `skip`, producing no entry (Req 4.7).
 *
 * Every optional identity field is included EXACTLY when populated, and omitted
 * otherwise (never `''`/null) (Req 2.5).
 */
export function normalizeChecklistRow(
  row: ChecklistRow,
  product: ProductDescriptor,
  category: string,
): NormalizeOutcome {
  const number = (row.number ?? '').trim();
  const name = (row.athlete ?? '').trim();
  const variation = (row.cardSet ?? '').trim();

  // Req 4.7: a missing required cell skips the row (no entry).
  if (number === '' || name === '' || variation === '') {
    return { kind: 'skip', reason: 'missing-required-cell' };
  }

  const team = (row.team ?? '').trim();

  // Product_Descriptor-derived display values (Req 4.3).
  const year = (product.year ?? '').trim();
  const brand = (product.brand ?? '').trim();
  const set = (product.product ?? '').trim();

  // SEQUENCE → serialTo (Req 4.4–4.6).
  let serialTo: number | undefined;
  let warning: 'bad-sequence' | undefined;
  const sequence = (row.sequence ?? '').trim();
  if (sequence !== '') {
    const parsed = Number(sequence);
    if (Number.isInteger(parsed) && parsed >= SERIAL_TO_MIN && parsed <= SERIAL_TO_INGEST_MAX) {
      serialTo = parsed;
    } else {
      warning = 'bad-sequence';
    }
  }

  // Core fields are always present; optional fields only when populated.
  const identity: CardIdentity = { category, set, number, name };
  assignIfPresent(identity, 'year', year);
  assignIfPresent(identity, 'brand', brand);
  assignIfPresent(identity, 'team', team);
  assignIfPresent(identity, 'variation', variation);
  if (serialTo !== undefined) {
    identity.serialTo = serialTo;
  }

  // Canonical Natural_Key: normalized product (PK) + normalized number (SK)
  // so the stored key matches a resolve-time lookup on both sides.
  const key: ChecklistNaturalKey = {
    year,
    brand,
    product: normalizeProductName(product.brand, product.product),
    number: normalizeNumber(number),
    cardSet: variation,
  };

  const entry: ChecklistEntry = { identity, key };
  const outcome: NormalizeOutcome = { kind: 'entry', entry };
  if (warning !== undefined) {
    outcome.warning = warning;
  }
  return outcome;
}

/**
 * Pure classification over the entries a `(product, number)` Query returned
 * (Req 7.2–7.4, 8.1–8.3). Defensively re-filters to entries whose normalized
 * product AND normalized number match the card's — even though the Query
 * already scoped by PK/SK — using `normalizeProductName` / `normalizeNumber` for
 * an exact, case-insensitive, whitespace-trimmed comparison (Req 7.1):
 *
 * - Exactly one match → `single`.
 * - Two or more → `multiple`, carrying every match with no omissions and no
 *   duplicates (Req 7.2).
 * - Zero matches, or an absent/empty card number after trimming → `none`
 *   (Req 7.4, 7.5).
 *
 * Never mutates the input array or its entries.
 */
export function resolveChecklist(
  entries: ChecklistEntry[],
  product: ProductDescriptor,
  number: string,
): ChecklistResolution {
  const wantNumber = normalizeNumber(number ?? '');
  // Req 7.5: no usable number anchor → empty result, no match.
  if (wantNumber === '') {
    return { kind: 'none' };
  }

  const wantProduct = normalizeProductName(product.brand, product.product);
  const matches = entries.filter(
    (entry) =>
      normalizeNumber(entry.identity.number) === wantNumber &&
      normalizeProductName(entry.key.brand, entry.key.product) === wantProduct,
  );

  if (matches.length === 0) {
    return { kind: 'none' };
  }
  const [first] = matches;
  if (matches.length === 1 && first !== undefined) {
    return { kind: 'single', entry: first };
  }
  return { kind: 'multiple', candidates: matches };
}

/**
 * Pure model of the guarded `checklistSuggestion` write: returns a NEW card
 * equal to `card` in every attribute EXCEPT `checklistSuggestion`, which is set
 * to `identity`. This is the property surface for the non-authoritative
 * invariant (Req 2.3, 2.4, 8.1, 8.4, 9.1–9.4) — the live handler realizes it via
 * a guarded, attribute-scoped `UpdateCommand`. Never mutates the input card.
 */
export function applyChecklistSuggestion<T extends { checklistSuggestion?: unknown }>(
  card: T,
  identity: CardIdentity,
): T {
  return { ...card, checklistSuggestion: identity };
}
